import { APEX_ROOT } from './authOrigin';

/**
 * SFTP logos sit under the apex docroot; DB path is `/images/{district}/...` (relative to apex).
 * On district subdomains, prefer same-origin tenant host when path slug matches current subdomain.
 * This avoids apex-host mismatches where `/images/{district}/...` is only available on the tenant host.
 * REACT_APP_LOGO_IMAGE_HOST=tenant → `https://{district}.backtoschoolforms.com/...` if files live per subdomain.
 * REACT_APP_IMAGE_ASSET_ORIGIN → full override origin.
 */
function getStaticSiteLogoOrigin(path, subdomainHint) {
  const explicit = (process.env.REACT_APP_IMAGE_ASSET_ORIGIN || '').trim().replace(/\/$/, '');
  if (explicit) {
    // Safety override: if explicit origin points to apex but we're on a matching tenant subdomain,
    // prefer tenant origin so district logos don't 404 on subdomain deployments.
    if (typeof window !== 'undefined') {
      const host = String(window.location.hostname || '').toLowerCase();
      const currentSubdomain = host.endsWith(`.${APEX_ROOT}`)
        ? host.slice(0, -(`.${APEX_ROOT}`.length))
        : '';
      const pathSlug = (String(path || '').match(/\/images\/([^/]+)\//)?.[1] || '').toLowerCase();
      const explicitHost = String(explicit).toLowerCase();
      const explicitIsApex = explicitHost === `https://${APEX_ROOT}` || explicitHost === `http://${APEX_ROOT}`;
      if (explicitIsApex && currentSubdomain && pathSlug && currentSubdomain === pathSlug) {
        return window.location.origin.replace(/\/$/, '');
      }
    }
    return explicit;
  }

  const mode = (process.env.REACT_APP_LOGO_IMAGE_HOST || 'apex').toLowerCase();
  if (mode === 'tenant') {
    const m = String(path || '').match(/\/images\/([^/]+)\//);
    const slugFromPath = m ? m[1] : '';
    const raw = String(subdomainHint || '').trim() || slugFromPath;
    const slug = raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (slug) {
      return `https://${slug}.${APEX_ROOT}`;
    }
  }

  // Auto mode: if we're already on the tenant subdomain that matches /images/{slug}/..., prefer same-origin.
  if (typeof window !== 'undefined') {
    const host = String(window.location.hostname || '').toLowerCase();
    const currentSubdomain = host.endsWith(`.${APEX_ROOT}`)
      ? host.slice(0, -(`.${APEX_ROOT}`.length))
      : '';
    const pathSlug = (String(path || '').match(/\/images\/([^/]+)\//)?.[1] || '').toLowerCase();
    if (currentSubdomain && pathSlug && currentSubdomain === pathSlug) {
      return window.location.origin.replace(/\/$/, '');
    }
  }

  return `https://${APEX_ROOT}`;
}

function maybeRewriteStaticLogoToCurrentTenant(url) {
  if (!url || typeof window === 'undefined') return url;

  try {
    const parsed = new URL(url);
    const pathSlug = (parsed.pathname.match(/\/images\/([^/]+)\//)?.[1] || '').toLowerCase();
    if (!pathSlug) return url;

    const currentHost = String(window.location.hostname || '').toLowerCase();
    const currentSubdomain = currentHost.endsWith(`.${APEX_ROOT}`)
      ? currentHost.slice(0, -(`.${APEX_ROOT}`.length))
      : '';
    if (!currentSubdomain || currentSubdomain !== pathSlug) return url;

    const logoHost = parsed.hostname.toLowerCase();
    const isApexHost = logoHost === APEX_ROOT || logoHost === `www.${APEX_ROOT}`;
    if (!isApexHost) return url;

    return `${window.location.origin.replace(/\/$/, '')}${parsed.pathname}`;
  } catch {
    return url;
  }
}

function getCurrentTenantOrigin() {
  if (typeof window === 'undefined') return '';
  const currentHost = String(window.location.hostname || '').toLowerCase();
  if (!currentHost.endsWith(`.${APEX_ROOT}`)) return '';
  return window.location.origin.replace(/\/$/, '');
}

function buildStaticLogoOriginCandidates(path, subdomainHint) {
  const candidates = [];

  const tenantFromPath = (String(path || '').match(/\/images\/([^/]+)\//)?.[1] || '').toLowerCase();
  const tenantFromHint = String(subdomainHint || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
  const tenantSlug = tenantFromHint || tenantFromPath;

  const currentTenantOrigin = getCurrentTenantOrigin();
  if (currentTenantOrigin) candidates.push(currentTenantOrigin);
  if (tenantSlug) candidates.push(`https://${tenantSlug}.${APEX_ROOT}`);
  candidates.push(`https://${APEX_ROOT}`);
  const explicit = (process.env.REACT_APP_IMAGE_ASSET_ORIGIN || '').trim().replace(/\/$/, '');
  if (explicit) candidates.push(explicit);

  // Stable dedupe, preserve order.
  return [...new Set(candidates.filter(Boolean))];
}

function buildStaticLogoPathCandidates(path) {
  const normalizedPath = String(path || '').trim();
  if (!normalizedPath) return [];

  const collected = [normalizedPath];
  if (normalizedPath.startsWith('/images/')) {
    collected.push(`/uploads${normalizedPath}`);
  } else if (normalizedPath.startsWith('/uploads/images/')) {
    collected.push(normalizedPath.replace(/^\/uploads/, ''));
  }

  const explicitPrefix = String(process.env.REACT_APP_STATIC_PATH_PREFIX || '')
    .trim()
    .replace(/^\/+|\/+$/g, '');

  const prefixes = [explicitPrefix].filter(Boolean);
  prefixes.forEach((prefix) => {
    if (!normalizedPath.startsWith(`/${prefix}/`)) {
      collected.push(`/${prefix}${normalizedPath}`);
    }
  });

  return [...new Set(collected)];
}

/**
 * Get the base URL for API requests and static assets (e.g. uploads).
 * Production: empty string = same-origin /api via nginx proxy.
 * Development (localhost): http://localhost:5001 unless REACT_APP_API_URL is set.
 */
export const getApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_URL;
  if (typeof configured === 'string' && configured.trim() !== '') {
    return configured.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location.hostname.includes('localhost')) {
    return 'http://localhost:5001';
  }
  return '';
};

/**
 * Get the full URL for an upload path.
 * - /images/... = apex absolute URL by default (see getStaticSiteLogoOrigin)
 * - /uploads/... = backend (local dev only for logos)
 */
function isStaticSiteLogoPath(path) {
  return typeof path === 'string' && /\/images\/[^/]+\/[^/]+$/.test(path);
}

export const getUploadUrl = (path, subdomainHint) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return maybeRewriteStaticLogoToCurrentTenant(path);
  }
  if (isStaticSiteLogoPath(path)) {
    return maybeRewriteStaticLogoToCurrentTenant(`${getStaticSiteLogoOrigin(path, subdomainHint)}${path}`);
  }
  const base = getApiBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

export const getDistrictLogoCandidates = (entity) => {
  if (!entity) return [];

  const collected = [];
  const direct = entity.logo_display_url;
  const rawPath = entity.logo_url || '';

  if (direct) {
    const rewritten = maybeRewriteStaticLogoToCurrentTenant(direct);
    collected.push(rewritten);
    try {
      const parsed = new URL(direct);
      if (isStaticSiteLogoPath(parsed.pathname)) {
        buildStaticLogoOriginCandidates(parsed.pathname, entity.subdomain).forEach((origin) => {
          collected.push(`${origin}${parsed.pathname}`);
        });
      }
    } catch {
      // ignore invalid absolute url
    }
  }

  if (rawPath) {
    if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
      collected.push(maybeRewriteStaticLogoToCurrentTenant(rawPath));
    } else if (isStaticSiteLogoPath(rawPath)) {
      const staticPathMatch = String(rawPath).match(/\/images\/([^/]+)\/([^/]+)$/);
      if (staticPathMatch) {
        const slug = staticPathMatch[1];
        const filename = staticPathMatch[2];
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        collected.push(`${apiBase}/api/districts/logo/${slug}/${encodeURIComponent(filename)}`);
      }

      const originCandidates = buildStaticLogoOriginCandidates(rawPath, entity.subdomain);
      const pathCandidates = buildStaticLogoPathCandidates(rawPath);
      originCandidates.forEach((origin) => {
        pathCandidates.forEach((pathCandidate) => {
          collected.push(`${origin}${pathCandidate}`);
        });
      });
      if (staticPathMatch) {
        const slug = staticPathMatch[1];
        const filename = staticPathMatch[2];
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        collected.push(`${apiBase}/uploads/logos/${slug}/${filename}`);
      }
    } else {
      const base = getApiBaseUrl();
      const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
      collected.push(`${base}${normalizedPath}`);
    }
  }

  return [...new Set(collected.filter(Boolean))];
};

/**
 * Prefer API-provided absolute URL (from FRONTEND_URL on server); else client-side getUploadUrl.
 */
export const getDistrictLogoSrc = (entity) => {
  return getDistrictLogoCandidates(entity)[0] || '';
};
